import React from 'react'
import { useDispatch } from 'react-redux'
import { saveAs } from 'file-saver'
import * as R from 'ramda'

import { importCandidates } from 'ducks/concurrency'
import { useConfirmationModal, useModal, useSnackbar } from 'hooks'
import { importCandidateSheet, templateDocument } from 'utils/sheet'
import { useImportCandidatesForm } from './common/ImportCandidatesForm/hooks'
import { FILE_FIELD } from './common/ImportCandidatesForm/model'

const useImportCandidatesMutations = () => {
  const dispatch = useDispatch()

  const handleImportCandidates = React.useCallback(
    ({ candidates, projectId }, { onShowSnackbar, onSuccess }) => {
      dispatch(
        importCandidates(
          { candidates, projectId },
          { onShowSnackbar, onSuccess },
        ),
      )
    },
    [dispatch],
  )

  return {
    handleImportCandidates,
  }
}

const useImportCandidatesData = state => {
  const { files } = state

  const [data, setData] = React.useState([])
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false)

  const handleDownloadTemplate = React.useCallback(async () => {
    const fileName = 'candidate_import_template.xlsx'

    try {
      const file = await templateDocument(fileName)

      saveAs(file, fileName)
    } catch (error) {
      console.log({ error })
    }
  }, [])

  const handlePreview = React.useCallback(() => {
    setIsPreviewOpen(R.not)
  }, [])

  React.useEffect(() => {
    const getPreview = async () => {
      try {
        const data = await importCandidateSheet(files ?? [])

        setData(data)
      } catch (error) {
        console.log({ error })
      }
    }

    getPreview()
  }, [files])

  return {
    state: { data, isPreviewOpen },
    handlers: { handleDownloadTemplate, handlePreview },
  }
}

export const useImportCandidatesState = (state, handlers) => {
  const { isOpen } = state
  const { onCancel, onClose, onConfirm } = handlers

  const {
    state: { control, formState, isDirtyOrInvalid },
    handlers: { handleFormReset, handleFormSubmit, watch },
  } = useImportCandidatesForm({ isOpen }, { onConfirm })

  const {
    handleConfirmationClose,
    handleConfirmationConfirm,
    handleConfirmationOpen,
    isConfirmationOpen,
  } = useConfirmationModal(() => {
    handleFormReset()
    onClose()
  })

  const files = watch(FILE_FIELD)

  const {
    state: { data, isPreviewOpen },
    handlers: { handleDownloadTemplate, handlePreview },
  } = useImportCandidatesData({ files })

  const handleCancel = React.useCallback(() => {
    isDirtyOrInvalid ? handleConfirmationOpen() : onCancel()
  }, [isDirtyOrInvalid, handleConfirmationOpen, onCancel])

  const handleClose = React.useCallback(() => {
    isDirtyOrInvalid ? handleConfirmationOpen() : onClose()
  }, [isDirtyOrInvalid, handleConfirmationOpen, onClose])

  const handleConfirm = React.useCallback(handleFormSubmit, [handleFormSubmit])

  return {
    state: {
      data,
      form: { control, formState },
      isConfirmationOpen,
      isPreviewOpen,
    },
    handlers: {
      handleCancel,
      handleClose,
      handleConfirm,
      handleConfirmationClose,
      handleConfirmationConfirm,
      handleDownloadTemplate,
      handlePreview,
    },
  }
}

export const useImportCandidatesModal = state => {
  const { projectId } = state

  const { handleClose, handleOpen, isOpen } = useModal()

  const {
    handlers: { handleShowSnackbar },
  } = useSnackbar()

  const { handleImportCandidates } = useImportCandidatesMutations()

  const handleConfirm = React.useCallback(
    ({ candidates }, { onSuccess }) => {
      handleImportCandidates(
        { candidates, projectId },
        { onShowSnackbar: handleShowSnackbar, onSuccess },
      )
      handleClose()
    },
    [projectId, handleClose, handleImportCandidates, handleShowSnackbar],
  )

  return {
    handleImportCandidatesModalClose: handleClose,
    handleImportCandidatesModalConfirm: handleConfirm,
    handleImportCandidatesModalOpen: handleOpen,
    isImportCandidatesModalOpen: isOpen,
  }
}
